import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExt from "@tiptap/extension-link";
import ImageExt from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Link2, Image as ImageIcon, Quote, Undo2, Redo2, Eraser, Loader2, Code, Film,
} from "lucide-react";
import { VideoEmbed } from "./VideoEmbedExtension";
import { videoEmbedUrl } from "../../lib/videoEmbeds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ToolbarButton({ active, onClick, title, children, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className={`p-2 rounded transition-colors ${
        active ? "bg-[#0F2040] text-white" : "text-gray-700 hover:bg-gray-100"
      } disabled:opacity-30`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Escriba aquí…", testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      ImageExt.configure({ HTMLAttributes: { class: "max-w-full h-auto my-4" } }),
      VideoEmbed,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g., switching between editing items)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Render a visible skeleton while Tiptap is initialising the editor —
  // otherwise the field would flash as empty white space, making it look as
  // if the editor is missing.
  if (!editor) {
    return (
      <div className="border border-gray-200 bg-white" data-testid={testId || "rte"}>
        <div className="border-b border-gray-200 bg-gray-50 h-10" />
        <div className="min-h-[300px] px-4 py-4 text-sm text-gray-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando editor…
        </div>
      </div>
    );
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL del enlace (vacío para quitar):", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertVideo = () => {
    const url = window.prompt("Pegue el enlace del vídeo (YouTube, Vimeo, Dailymotion):", "https://");
    if (!url) return;
    const src = videoEmbedUrl(url);
    if (!src) {
      alert("Enlace de vídeo no reconocido. Use un enlace de YouTube, Vimeo o Dailymotion.");
      return;
    }
    editor.chain().focus().setVideoEmbed(src).run();
  };

  const onPickImage = () => fileRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: absolute, alt: file.name }).run();
    } catch (err) {
      alert("Error al subir la imagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid={testId || "rte"}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Cursiva (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Tachado">
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Título 2">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Título 3">
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista con viñetas">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cita">
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Bloque de código">
          <Code className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton active={editor.isActive("link")} onClick={setLink} title="Insertar enlace">
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={onPickImage} title="Insertar imagen" disabled={uploading}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </ToolbarButton>
        <ToolbarButton onClick={insertVideo} title="Insertar vídeo (YouTube, Vimeo…)" testId="rte-video-btn">
          <Film className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpiar formato">
          <Eraser className="w-4 h-4" />
        </ToolbarButton>
        <span className="ml-auto flex">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer" disabled={!editor.can().undo()}>
            <Undo2 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer" disabled={!editor.can().redo()}>
            <Redo2 className="w-4 h-4" />
          </ToolbarButton>
        </span>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-4 py-4 min-h-[300px] focus:outline-none [&_*]:focus:outline-none rte-content"
      />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChosen} />
    </div>
  );
}
